import * as Sentry from "@sentry/react";

/**
 * Inicializa Sentry para el monitoreo de errores
 */
export const initSentry = () => {
  // Solo inicializar Sentry si hay un DSN configurado
  const dsn = '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';

  if (!dsn) {
    console.warn('Sentry DSN no configurado. El monitoreo de errores está deshabilitado.');
    return;
  }

  Sentry.init({
    dsn,

    // Entorno (development, staging, production)
    environment: import.meta.env.VITE_ENV || import.meta.env.MODE || 'development',

    // Release tracking - importante para seguir errores por versión
    release: `housekeeping-frontend@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

    // Performance Monitoring
    integrations: [
      // Integración de React Router para tracking de navegación
      Sentry.browserTracingIntegration(),

      // Captura interacciones del usuario
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),

      // Captura errores de HTTP
      Sentry.httpClientIntegration(),
    ],

    // Tasa de muestreo de rendimiento (0.0 - 1.0)
    // En producción, usa un valor más bajo para reducir costos
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Tasa de muestreo de sesiones para replay (0.0 - 1.0)
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.5,

    // Tasa de muestreo cuando hay un error
    replaysOnErrorSampleRate: 1.0,

    // Filtrar errores conocidos o irrelevantes
    beforeSend(event) {
      // Filtrar errores de extensiones del navegador
      if (event.exception) {
        const exceptionValue = event.exception.values?.[0]?.value || '';

        // Ignorar errores de extensiones
        if (exceptionValue.includes('Extension') ||
            exceptionValue.includes('chrome-extension')) {
          return null;
        }

        // Ignorar errores de red comunes en desarrollo
        if (!import.meta.env.PROD && exceptionValue.includes('NetworkError')) {
          return null;
        }
      }

      return event;
    },

    // Agregar contexto adicional
    initialScope: {
      tags: {
        'app.type': 'pwa',
      },
    },

    // Habilitar depuración en desarrollo
    debug: import.meta.env.DEV,

    // Normalizar URLs para mejor agrupación de errores
    normalizeDepth: 10,
  });
};

/**
 * Configura el contexto del usuario en Sentry
 * @param {Object} user - Datos del usuario
 */
export const setSentryUser = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
    Sentry.setTag('user.role', user.role);
    Sentry.setTag('establishment.id', user.establishment_id || 'none');
    if (user.establishment?.name) {
      Sentry.setTag('establishment.name', user.establishment.name);
    }
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user.role', null);
    Sentry.setTag('establishment.id', null);
    Sentry.setTag('establishment.name', null);
  }
};

/**
 * Captura un error manualmente
 * @param {Error} error - El error a capturar
 * @param {Object} context - Contexto adicional
 */
export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Captura un mensaje
 * @param {string} message - El mensaje a capturar
 * @param {string} level - Nivel del mensaje (info, warning, error)
 */
export const captureMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Agregar breadcrumb (rastro de navegación)
 * @param {Object} breadcrumb - Datos del breadcrumb
 */
export const addBreadcrumb = (breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

export default Sentry;
